import fromUnixTime from 'date-fns/fromUnixTime';
import differenceInDays from 'date-fns/differenceInDays';
import Cookies from 'js-cookie';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { emitter } from 'shared/helpers/mitt';
import { clearLoggedOutCookie, markLoggedOut } from 'v3/helpers/AuthHelper';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../constants/appEvents';

Cookies.defaults = { sameSite: 'Lax' };

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  emitter.emit(CHATWOOT_SET_USER, { user });
  emitter.emit(ANALYTICS_IDENTITY, { user });
};

// Persist only DeviseTokenAuth credentials. Stringifying the full Axios
// response.headers can exceed the ~4KB cookie limit in development (e.g.
// MiniProfiler headers), so the browser silently drops the cookie and the
// user is bounced back to /app/login right after a successful sign-in.
const readHeader = (headers, key) => {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') {
    return headers.get(key) || headers.get(key.toLowerCase());
  }
  return headers[key] || headers[key.toLowerCase()];
};

export const getHeaderExpiry = response => {
  const expiry = readHeader(response.headers, 'expiry');
  if (!expiry) return null;

  const expiryDate = fromUnixTime(Number(expiry));
  return Number.isNaN(expiryDate.getTime()) ? null : expiryDate;
};

const AUTH_CREDENTIAL_KEYS = [
  'access-token',
  'token-type',
  'client',
  'expiry',
  'uid',
];

export const extractAuthHeaders = (headers = {}) => ({
  'access-token': readHeader(headers, 'access-token'),
  'token-type': readHeader(headers, 'token-type'),
  client: readHeader(headers, 'client'),
  expiry: readHeader(headers, 'expiry'),
  uid: readHeader(headers, 'uid'),
});

// Proxies may expose only some DeviseTokenAuth headers. Prefer each header
// value when present, and fill the rest from credentials in the JSON body.
export const mergeAuthCredentials = (fromHeaders = {}, fromBody = {}) => {
  const merged = {};
  AUTH_CREDENTIAL_KEYS.forEach(key => {
    const value = fromHeaders[key] || fromBody[key];
    if (value) merged[key] = value;
  });
  return merged;
};

export const setAuthCredentials = response => {
  const fromHeaders = extractAuthHeaders(response.headers);
  // Fallback: auth credentials embedded in JSON body (Cloudflare strips DTA headers)
  const fromBody = extractAuthHeaders(response.data || {});
  const authHeaders = mergeAuthCredentials(fromHeaders, fromBody);

  if (!authHeaders['access-token']) return;

  const expiryDate = (() => {
    if (!authHeaders.expiry) return null;
    const date = fromUnixTime(Number(authHeaders.expiry));
    return Number.isNaN(date.getTime()) ? null : date;
  })();

  const cookieValue = JSON.stringify(authHeaders);
  const expiresDays = expiryDate
    ? Math.max(differenceInDays(expiryDate, new Date()), 1)
    : 60;

  Cookies.set('cw_d_session_info', cookieValue, {
    expires: expiresDays,
  });
  clearLoggedOutCookie();

  setUser(response.data.data, expiryDate);
};

export const clearBrowserSessionCookies = () => {
  Cookies.remove('cw_d_session_info');
  Cookies.remove('auth_data');
  Cookies.remove('user');
};

export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
};

export const clearSessionStorageOnLogout = () => {
  SessionStorage.remove(SESSION_STORAGE_KEYS.IMPERSONATION_USER);
};

export const deleteIndexedDBOnLogout = async () => {
  let dbs = [];
  try {
    dbs = await window.indexedDB.databases();
    dbs = dbs.map(db => db.name);
  } catch (e) {
    dbs = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
  }

  dbs.forEach(dbName => {
    const deleteRequest = window.indexedDB.deleteDatabase(dbName);

    deleteRequest.onerror = event => {
      // eslint-disable-next-line no-console
      console.error(`Error deleting database ${dbName}.`, event);
    };

    deleteRequest.onsuccess = () => {
      // eslint-disable-next-line no-console
      console.log(`Database ${dbName} deleted successfully.`);
    };
  });

  localStorage.removeItem('cw-idb-names');
};

export const resolveLogoutRedirectLink = configured => {
  if (!configured || configured === '/' || configured === '/app/login') {
    return '/app/login?leadster_sso=skip';
  }
  return configured;
};

export const clearCookiesOnLogout = () => {
  emitter.emit(CHATWOOT_RESET);
  emitter.emit(ANALYTICS_RESET);
  clearBrowserSessionCookies();
  markLoggedOut();
  clearLocalStorageOnLogout();
  clearSessionStorageOnLogout();
  const globalConfig = window.globalConfig || {};
  window.location = resolveLogoutRedirectLink(
    globalConfig.LOGOUT_REDIRECT_LINK
  );
};

export const parseAPIErrorResponse = error => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  }
  if (error?.response?.data?.error) {
    return error?.response?.data?.error;
  }
  if (error?.response?.data?.errors) {
    return error?.response?.data?.errors[0];
  }
  return error;
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  throw new Error(errorMessage);
};

export const parseLinearAPIErrorResponse = (error, defaultMessage) => {
  const errorData = error.response.data;
  const errorMessage = errorData?.error?.errors?.[0]?.message || defaultMessage;
  return errorMessage;
};
